import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import type { FreeShippingApplyMethod, FreeShippingCalcMode } from '@/api/types/settings';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { SettingsRow } from '../components/SettingsSection';

interface FreeShippingSectionProps {
  calcMode: FreeShippingCalcMode;
  applyMethod: FreeShippingApplyMethod;
}

/**
 * Free-shipping behaviour. Per-market activation lives in Widgets → Free
 * shipping; these two settings control how the threshold is evaluated and how
 * the discount is applied store-wide. Each select saves immediately on change.
 */
export function FreeShippingSection({ calcMode, applyMethod }: FreeShippingSectionProps) {
  const { t } = useTranslation('settings');

  const updateCalcMode = useUpdateMerchantSettings();
  const updateApplyMethod = useUpdateMerchantSettings();

  const saveCalcMode = (value: string) => {
    if (value === calcMode || updateCalcMode.isPending) return;
    updateCalcMode.mutate(
      { settings: { free_shipping_calc_mode: value as FreeShippingCalcMode } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  const saveApplyMethod = (value: string) => {
    if (value === applyMethod || updateApplyMethod.isPending) return;
    updateApplyMethod.mutate(
      { settings: { free_shipping_apply_method: value as FreeShippingApplyMethod } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  return (
    <SettingsRow
      label={t('checkout.freeShipping.title')}
      description={t('checkout.freeShipping.description')}
    >
      <div className="flex flex-wrap gap-2">
        <Select value={calcMode} onValueChange={saveCalcMode}>
          <SelectTrigger className="w-auto gap-2" aria-label={t('checkout.freeShipping.calcMode')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              value="before_discounts"
              description={t('checkout.freeShipping.calcModes.beforeDiscounts.description')}
            >
              {t('checkout.freeShipping.calcModes.beforeDiscounts.label')}
            </SelectItem>
            <SelectItem
              value="after_discounts"
              description={t('checkout.freeShipping.calcModes.afterDiscounts.description')}
            >
              {t('checkout.freeShipping.calcModes.afterDiscounts.label')}
            </SelectItem>
          </SelectContent>
        </Select>
        <Select value={applyMethod} onValueChange={saveApplyMethod}>
          <SelectTrigger
            className="w-auto gap-2"
            aria-label={t('checkout.freeShipping.applyMethod')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              value="carrier"
              description={t('checkout.freeShipping.applyMethods.carrier.description')}
            >
              {t('checkout.freeShipping.applyMethods.carrier.label')}
            </SelectItem>
            <SelectItem
              value="discount_function"
              description={t('checkout.freeShipping.applyMethods.discountFunction.description')}
            >
              {t('checkout.freeShipping.applyMethods.discountFunction.label')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </SettingsRow>
  );
}
